import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

export interface ApprovalStatus {
  hasProfile: boolean;
  isApproved: boolean;
  loading: boolean;
}

// Cache approval status to avoid repeated DB queries
const approvalCache = new Map<string, { status: ApprovalStatus; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache

export function useApprovalStatus(): ApprovalStatus {
  const { user, loading: authLoading } = useAuth();
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus>({
    hasProfile: false,
    isApproved: false,
    loading: true
  });
  const checkedRef = useRef(false);

  useEffect(() => {
    async function checkApprovalStatus() {
      if (authLoading) return;
      
      if (!user) {
        setApprovalStatus({ hasProfile: false, isApproved: false, loading: false });
        return;
      }

      // Check cache first
      const cached = approvalCache.get(user.id);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        setApprovalStatus(cached.status);
        return;
      }

      // Prevent duplicate checks
      if (checkedRef.current) return;
      checkedRef.current = true;

      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('registration_approved')
          .eq('user_id', user.id)
          .maybeSingle();

        const status: ApprovalStatus = !profile 
          ? { hasProfile: false, isApproved: false, loading: false }
          : {
              hasProfile: true,
              isApproved: profile.registration_approved || false,
              loading: false
            };

        // Cache the result
        approvalCache.set(user.id, { status, timestamp: Date.now() });
        setApprovalStatus(status);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Error checking approval status:', error);
        }
        setApprovalStatus({ hasProfile: false, isApproved: false, loading: false });
      }
    }

    checkedRef.current = false;
    checkApprovalStatus();
  }, [user?.id, authLoading]);

  return approvalStatus;
}

// Export function to clear cache (useful after profile updates)
export function clearApprovalCache(userId?: string) {
  if (userId) {
    approvalCache.delete(userId);
  } else {
    approvalCache.clear();
  }
}
